
import { Children, isValidElement, type ReactNode, type HTMLAttributes } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card as ShadcnCard } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { LayoutDirection } from '@/types/workflow-graph';

// ============================================================================
// WorkflowCard Props (following the Card abstraction pattern)
// ============================================================================

export interface WorkflowCardProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    /** Whether the card is selectable */
    selectable?: boolean;
    /** Whether the node is selected in ReactFlow */
    selected?: boolean;
    /** Accent color for left border (e.g., 'border-l-blue-500') */
    accentColor?: string;
    /** Whether this is the start node */
    isStart?: boolean;
    /** Whether this is a terminal node */
    isEnd?: boolean;
    /** Handle color class */
    handleColor?: string;
    /** Layout direction - determines handle positions (LR=left/right, TB=top/bottom) */
    layoutDirection?: LayoutDirection;
    /** Debug mode - current node in debug session */
    isCurrentNode?: boolean;
    /** Whether this node has multiple outgoing edges requiring dynamic routing */
    isDynamicRoute?: boolean;
    /** Whether the dynamic Next JSONPath has been configured */
    isDynamicNextConfigured?: boolean;
    /** Callback when user clicks the diamond handle to configure dynamic routing */
    onDynamicHandleClick?: () => void;
}

// Card dimensions config for ReactFlow node sizing (matching card-org-chart pattern)
export const workflowCardConfig = {
    width: 288,
    height: 128,
    className: 'w-72 min-h-32 max-h-32',
};

// ============================================================================
// Helper to extract child components by type
// ============================================================================

function extractChild(children: ReactNode, component: React.ComponentType<CardSlotProps>): ReactNode | null {
    const found = Children.toArray(children).filter(
        (child) => isValidElement(child) && child.type === component
    );
    return found.length > 0 ? found : null;
}

// ============================================================================
// Sub-components (matching Card abstraction)
// ============================================================================

interface CardSlotProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    className?: string;
}

export const WorkflowCardIcon = ({ children, className, ...props }: CardSlotProps) => (
    <div
        className={cn('flex shrink-0 items-center justify-center', className)}
        {...props}
    >
        {children}
    </div>
);

export const WorkflowCardName = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('text-left text-sm font-bold pb-1', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardDescription = ({ children, className, ...props }: CardSlotProps) => (
    <div className="w-10/12 overflow-hidden text-ellipsis break-words" {...props}>
        <span className={cn('text-left text-xs text-muted-foreground pb-2 leading-tight', className)}>
            {children}
        </span>
    </div>
);

export const WorkflowCardSubtitle = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('font-mono text-[11px] text-slate-500 leading-tight -mt-1 mb-2', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardBadge = ({ children, className, ...props }: CardSlotProps) => (
    <span
        className={cn(
            'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            'bg-green-100 text-green-700',
            className
        )}
        {...props}
    >
        {children}
    </span>
);

export const WorkflowCardTopRight = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute right-1 top-1 z-10', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardTopLeft = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute left-1 top-1 z-10', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardBottomLeft = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute bottom-1 left-1 z-10', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardBottomCenter = ({ children, className, ...props }: CardSlotProps) => (
    <div
        className={cn('absolute bottom-1 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center', className)}
        {...props}
    >
        {children}
    </div>
);

export const WorkflowCardBottomRight = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute bottom-1 right-1 z-10', className)} {...props}>
        {children}
    </div>
);

export const WorkflowCardMiddleRight = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute right-1 top-1/2 z-10 -translate-y-1/2', className)} {...props}>
        {children}
    </div>
);

// ============================================================================
// WorkflowCard Component (following Card abstraction pattern)
// ============================================================================

// Helper to get remaining children that weren't extracted
function getRemainingChildren(children: ReactNode, extractedTypes: React.ComponentType<CardSlotProps>[]): ReactNode[] {
    return Children.toArray(children).filter(
        (child) => !isValidElement(child) || !extractedTypes.includes(child.type as React.ComponentType<CardSlotProps>)
    );
}

export function WorkflowCard({
    children,
    className,
    selectable = false,
    selected = false,
    accentColor = 'border-l-blue-500',
    isStart: _isStart = false, // Kept for API consistency; handles are always shown
    isEnd: _isEnd = false, // Kept for API consistency; handles are always shown
    handleColor = '!bg-slate-400 !border-slate-500',
    layoutDirection = 'LR',
    isCurrentNode = false,
    isDynamicRoute = false,
    isDynamicNextConfigured = false,
    onDynamicHandleClick,
    ...props
}: WorkflowCardProps) {
    // Extract positioned children
    const icon = extractChild(children, WorkflowCardIcon);
    const name = extractChild(children, WorkflowCardName);
    const subtitle = extractChild(children, WorkflowCardSubtitle);
    const description = extractChild(children, WorkflowCardDescription);
    const badge = extractChild(children, WorkflowCardBadge);
    const topLeft = extractChild(children, WorkflowCardTopLeft);
    const topRight = extractChild(children, WorkflowCardTopRight);
    const middleRight = extractChild(children, WorkflowCardMiddleRight);
    const bottomLeft = extractChild(children, WorkflowCardBottomLeft);
    const bottomCenter = extractChild(children, WorkflowCardBottomCenter);
    const bottomRight = extractChild(children, WorkflowCardBottomRight);
    
    // Get any remaining children that weren't extracted (e.g., BreakpointMarker)
    const remainingChildren = getRemainingChildren(children, [
        WorkflowCardIcon,
        WorkflowCardName,
        WorkflowCardSubtitle,
        WorkflowCardDescription,
        WorkflowCardBadge,
        WorkflowCardTopLeft,
        WorkflowCardTopRight,
        WorkflowCardMiddleRight,
        WorkflowCardBottomLeft,
        WorkflowCardBottomCenter,
        WorkflowCardBottomRight,
    ]);

    // Determine handle positions based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;

    // Adjust accent border based on layout direction
    const accentBorderClass = isHorizontal ? 'border-l-4' : 'border-t-4';
    const accentColorClass = isHorizontal ? accentColor : accentColor.replace('border-l-', 'border-t-');

    return (
        <ShadcnCard
            className={cn(
                workflowCardConfig.className,
                'relative border-2 border-slate-500 p-2',
                accentBorderClass,
                accentColorClass,
                'text-foreground/75 transition-all duration-200 hover:cursor-pointer hover:bg-muted hover:text-foreground',
                // Shadow glow on hover (matching Card pattern)
                'shadow-[0_0_0_0_rgba(59,130,246,0)] hover:shadow-[0_0_15px_2px_rgba(59,130,246,0.5)]',
                // Selection state (matching Card pattern)
                selected && 'bg-gradient-to-br from-muted to-blue-100 border-2 border-blue-600',
                // Debug highlight - pulsing orange glow when current node
                isCurrentNode && 'ring-4 ring-orange-400 ring-opacity-75 animate-pulse border-orange-500 shadow-[0_0_20px_5px_rgba(251,146,60,0.5)]',
                className
            )}
            {...props}
        >
            {/* Input Handle - position based on layout direction */}
            {/* Always show input handle - even for start nodes (StartMarker connects to them) */}
            <Handle 
                type="target"
                id="target"
                position={inputPosition} 
                className={cn(handleColor, '!h-3 !w-3')} 
            />

            {/* Row container: Icon (top-left) + Content (name/description stacked) */}
            <div className="flex flex-row items-start gap-3 pt-1">
                {/* Icon - top left */}
                {icon}

                {/* Content area - name on top, subtitle (code), description below */}
                <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                        {name}
                        {badge}
                    </div>
                    {subtitle}
                    {description}
                </div>
            </div>

            {/* Positioned action slots */}
            {topLeft}
            {topRight}
            {middleRight}
            {bottomLeft}
            {bottomCenter}
            {bottomRight}

            {/* Output Handle - position based on layout direction */}
            {/* When isDynamicRoute is true, renders as a diamond shape for dynamic routing */}
            {isDynamicRoute ? (
                <div
                    className={cn(
                        'absolute z-10 cursor-pointer',
                        isHorizontal
                            ? 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2'
                            : 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2'
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDynamicHandleClick?.();
                    }}
                    title={isDynamicNextConfigured ? 'Edit dynamic routing' : 'Configure dynamic routing (required)'}
                >
                    <div
                        className={cn(
                            'flex h-5 w-5 rotate-45 items-center justify-center rounded-sm border-2 transition-colors',
                            isDynamicNextConfigured
                                ? 'border-cyan-600 bg-cyan-100 hover:bg-cyan-200'
                                : 'border-red-600 bg-red-100 hover:bg-red-200 animate-pulse'
                        )}
                    >
                        <Handle
                            type="source"
                            id="source"
                            position={outputPosition}
                            className="!h-1 !w-1 !border-0 !bg-transparent !-rotate-45"
                            style={{ position: 'relative', transform: 'none', top: 'auto', left: 'auto' }}
                        />
                    </div>
                </div>
            ) : (
                <Handle 
                    type="source"
                    id="source"
                    position={outputPosition} 
                    className={cn(handleColor, '!h-3 !w-3')} 
                />
            )}
            
            {/* Render any remaining children (e.g., BreakpointMarker) */}
            {remainingChildren}
        </ShadcnCard>
    );
}

// Attach sub-components for compound pattern (matching Card pattern)
WorkflowCard.Icon = WorkflowCardIcon;
WorkflowCard.Name = WorkflowCardName;
WorkflowCard.Subtitle = WorkflowCardSubtitle;
WorkflowCard.Description = WorkflowCardDescription;
WorkflowCard.Badge = WorkflowCardBadge;
WorkflowCard.TopLeft = WorkflowCardTopLeft;
WorkflowCard.TopRight = WorkflowCardTopRight;
WorkflowCard.BottomLeft = WorkflowCardBottomLeft;
WorkflowCard.BottomRight = WorkflowCardBottomRight;
WorkflowCard.MiddleRight = WorkflowCardMiddleRight;
WorkflowCard.BottomCenter = WorkflowCardBottomCenter;
